import type { FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { organizationsApi } from '@/entities/organizations/api';
import {
  type ClarityConnection,
  type ClarityConnectionUpdate,
  type ClarityLookup,
  type ClarityProfileOptIn,
  decodeClarityConnection,
  decodeClarityLookup,
  decodeClarityProfileOptIn,
} from './types';

/**
 * Returned instead of the raw body when a response does not decode. It carries no
 * response data, so an unexpected token body is never stored or surfaced.
 */
const UNEXPECTED_RESPONSE: FetchBaseQueryError = { status: 'CUSTOM_ERROR', error: 'unexpected_response' };

type BaseQueryResult = { data?: unknown; error?: unknown };
type BaseQuery = (arg: string | FetchArgs) => BaseQueryResult | PromiseLike<BaseQueryResult>;

/** Runs the shared admin base query (so the global 401 handling applies) and decodes the body. */
async function decoded<T>(
  baseQuery: BaseQuery,
  args: string | FetchArgs,
  decode: (body: unknown) => T | null,
): Promise<{ data: T } | { error: FetchBaseQueryError }> {
  const result = await baseQuery(args);
  if (result.error) return { error: result.error as FetchBaseQueryError };
  const value = decode(result.data);
  return value === null ? { error: UNEXPECTED_RESPONSE } : { data: value };
}

const connectionUrl = (organizationId: number) => `/clarity/organizations/${organizationId}/connection`;

const forOrganization = (organizationId: number) => (body: unknown) => {
  const connection = decodeClarityConnection(body);
  return connection?.organization_id === organizationId ? connection : null;
};

const forProfile = (profileId: number) => (body: unknown) => {
  const optIn = decodeClarityProfileOptIn(body);
  return optIn?.profileId === profileId ? optIn : null;
};

export const clarityApi = organizationsApi.enhanceEndpoints({
  addTagTypes: ['ClarityConnection', 'ClarityProfileOptIn'],
}).injectEndpoints({
  endpoints: (b) => ({
    getClarityConnection: b.query<ClarityConnection, number>({
      queryFn: (organizationId, _api, _extra, baseQuery) =>
        decoded(baseQuery, connectionUrl(organizationId), forOrganization(organizationId)),
      providesTags: (_result, _error, organizationId) => [{ type: 'ClarityConnection', id: organizationId }],
    }),
    updateClarityConnection: b.mutation<ClarityConnection, { organizationId: number; body: ClarityConnectionUpdate }>({
      queryFn: ({ organizationId, body }, _api, _extra, baseQuery) =>
        decoded(baseQuery, { url: connectionUrl(organizationId), method: 'PUT', body }, forOrganization(organizationId)),
      // Counts are computed live on the PUT response too, so write it into the cache instead of refetching.
      async onQueryStarted({ organizationId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(clarityApi.util.updateQueryData('getClarityConnection', organizationId, () => data));
        } catch {
          // The component renders the error; nothing to update.
        }
      },
    }),
    lookupClarityOrganization: b.mutation<ClarityLookup, { organizationId: number; clarityOrganizationId: string }>({
      queryFn: ({ organizationId, clarityOrganizationId }, _api, _extra, baseQuery) =>
        decoded(baseQuery, {
          url: `${connectionUrl(organizationId)}/lookup`,
          method: 'GET',
          params: { clarity_organization_id: clarityOrganizationId },
        }, decodeClarityLookup(clarityOrganizationId)),
    }),
    connectClarity: b.mutation<ClarityConnection, { organizationId: number; clarityOrganizationId: string }>({
      queryFn: ({ organizationId, clarityOrganizationId }, _api, _extra, baseQuery) =>
        decoded(baseQuery, {
          url: `${connectionUrl(organizationId)}/connect`,
          method: 'POST',
          body: { clarity_organization_id: clarityOrganizationId },
        }, forOrganization(organizationId)),
      async onQueryStarted({ organizationId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(clarityApi.util.updateQueryData('getClarityConnection', organizationId, () => data));
        } catch {
          // The component renders the error; keep the current connection.
        }
      },
    }),
    disconnectClarity: b.mutation<ClarityConnection, number>({
      queryFn: (organizationId, _api, _extra, baseQuery) =>
        decoded(baseQuery, { url: `${connectionUrl(organizationId)}/disconnect`, method: 'POST' }, forOrganization(organizationId)),
      async onQueryStarted(organizationId, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(clarityApi.util.updateQueryData('getClarityConnection', organizationId, () => data));
        } catch {
          // The component renders the error; keep the current connection.
        }
      },
    }),
    getClarityProfileOptIn: b.query<ClarityProfileOptIn, number>({
      queryFn: (profileId, _api, _extra, baseQuery) =>
        decoded(baseQuery, `/profiles/${profileId}`, forProfile(profileId)),
      providesTags: (_result, _error, profileId) => [{ type: 'ClarityProfileOptIn', id: profileId }],
    }),
    updateClarityProfileOptIn: b.mutation<ClarityProfileOptIn, { profileId: number; enabled: boolean }>({
      queryFn: ({ profileId, enabled }, _api, _extra, baseQuery) =>
        decoded(
          baseQuery,
          { url: `/profiles/${profileId}`, method: 'PATCH', body: { clarity_chatbot_enabled: enabled } },
          forProfile(profileId),
        ),
      async onQueryStarted({ profileId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(clarityApi.util.updateQueryData('getClarityProfileOptIn', profileId, () => data));
        } catch {
          // The component renders the error and keeps the cached value.
        }
      },
    }),
  }),
});

export const {
  useGetClarityConnectionQuery,
  useUpdateClarityConnectionMutation,
  useLookupClarityOrganizationMutation,
  useConnectClarityMutation,
  useDisconnectClarityMutation,
  useGetClarityProfileOptInQuery,
  useUpdateClarityProfileOptInMutation,
} = clarityApi;
